import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Link,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as RegisterIcon,
  PersonRemove as UnregisterIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  TableRestaurant as TableIcon,
  SwapHoriz as SubstitutionIcon,
  PlaylistAddCheck as AttendanceIcon,
} from '@mui/icons-material';
import { EventDetails, EventParticipant, ApprovalStatus, ApproveReservationRequest } from '../../types/calendar';
import { calendarService } from '../../services/calendarService';
import eventService from '../../services/eventService';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';
// A szerepkör-döntések EGY helyen, a GÉPI névből [B-W4, 2026-09-16] — lásd `utils/roles.ts`.
import { isCoachRole } from '../../utils/roles';
// [Web UX 1. csomag, 2026-10-03] Emberi hibaszöveg: hálózati hiba, üzenet nélküli 401/403/429
// és a modell-validációs törzs is magyar mondatot ad (a nyers `err.response?.data?.message`
// ezeknél „Network Error"-t vagy semmit sem adott).
// [3. javító kör, F-1, 2026-10-03] Lejárat-tudatos burkolók (`utils/saveErrors.ts`): az elutasítás
// okának MENTÉSE zárral a „megmaradtak" mondatot adja, a többi művelet a rövid lejárat-mondatot.
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
// [magánóra-sorozat, 2026-10-31] „Sorozat törlése innentől": a saját-sorozat döntés és a visszajelzés közös segédből.
import { describeSeriesDeleteResult, isSeriesOwnedBy } from '../../utils/eventSeries';
import type { Theme } from '@mui/material/styles';
import { rejectionPanelColors } from '../../theme/readableColors';

interface EventDetailsDialogProps {
  open: boolean;
  eventId: string | null;
  onClose: () => void;
  onEdit?: (eventId: string) => void;
  onDelete?: (eventId: string) => void;
  onEventUpdated?: () => void;
  onNavigateToUserProfile?: (userId: string) => void;
  /**
   * A jelenléti ívre visz, ARRA az eseményre. A klub kérése: mobilon a jelenlét az
   * eseményen belül egy koppintás, a weben eddig külön fülre kellett menni és ott
   * dátumot + eseményt újra kikeresni.
   */
  onNavigateToAttendance?: (eventId: string, eventDate: string) => void;
}

const EventDetailsDialog: React.FC<EventDetailsDialogProps> = ({
  open,
  eventId,
  onClose,
  onEdit,
  onDelete,
  onEventUpdated,
  onNavigateToUserProfile,
  onNavigateToAttendance,
}) => {
  const [eventDetails, setEventDetails] = useState<EventDetails | null>(null);
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [actionLoading, setActionLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  /**
   * [magánóra-sorozat, 2026-10-31] Az esemény sorozatának tagjai a bejelentkezett felhasználó szemével
   * (`GET /events?recurringEventGroupId=`) — ebből dől el, hogy edzőnek „saját sorozat"-e (minden tag `createdBy`-ja ő).
   * `null` = nem töltött be / nem sorozat → edzőnek nincs gomb. A szerver a mérvadó (403).
   */
  const [seriesMembers, setSeriesMembers] = useState<Array<{ createdBy?: string }> | null>(null);
  const { user } = useAuth();
  const confirm = useConfirm();
  const { showSuccess, showInfo } = useFeedback();

  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] Lelet: `EventDetailsDialog.tsx:536`
   * (korábbi sor, `showRejectForm`) — az asztalfoglalás elutasítási oka a háttér-kattintásra/
   * Esc-re/„Bezárás”-ra kérdés nélkül elveszett. Piszkos = nyitott dialógus + nyitott elutasítási
   * űrlap + nem üres ok (trim után); az üres mező NEM kérdez.
   */
  const rejectReasonDirty = open && showRejectForm && isFormDirty({ reason: '' }, { reason: rejectionReason });
  useUnsavedChanges(rejectReasonDirty, 'Elutasítás oka');

  // Check if current user can view other users' profiles
  const canViewProfiles = user?.userRole === 'Administrator' || user?.userRole === 'Coach';

  const handleNavigateToProfile = (userId: string) => {
    // Don't close this dialog - just call the callback to open the profile dialog
    if (onNavigateToUserProfile) {
      onNavigateToUserProfile(userId);
    }
  };

  useEffect(() => {
    if (open && eventId) {
      loadEventDetails();
      loadParticipants();
    }
  }, [open, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEventDetails = async () => {
    if (!eventId) return;
    
    try {
      setLoading(true);
      setError('');
      const details = await calendarService.getEventById(eventId);
      setEventDetails(details);
      setSeriesMembers(null);
      // [magánóra-sorozat, 2026-10-31] Csak admin/edző törölhet sorozatot; a játékos felé nincs plusz kérés.
      if (details.recurringEventGroupId && (user?.userRole === 'Administrator' || user?.userRole === 'Coach')) {
        try {
          setSeriesMembers(await eventService.getEvents({ recurringEventGroupId: details.recurringEventGroupId }));
        } catch (seriesErr) {
          console.error('Failed to load the recurring series:', seriesErr);
          setSeriesMembers(null);
        }
      }
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt az esemény részleteinek betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    if (!eventId) return;
    
    try {
      const participantList = await calendarService.getEventParticipants(eventId);
      setParticipants(participantList);
      
      // Check if current user is registered
      const currentUserRegistered = participantList.some(p => p.userId === user?.userId);
      setIsRegistered(currentUserRegistered);
    } catch (err) {
      console.error('Error loading participants:', err);
    }
  };

  const handleRegister = async () => {
    if (!eventId || !user) return;

    try {
      setActionLoading(true);
      setError('');
      // For simplicity, we'll use a default role. In a real app, you might want to show a role selector
      await calendarService.registerForEvent(eventId, user.userRoleId);
      // [Web UX 1. csomag, 2026-10-03] Visszajelzés: eddig a siker után csak újratöltött, a
      // játékos nem kapott megerősítést (a többi művelet ugyanebben a fájlban már toastot ad).
      showSuccess('Jelentkeztél az edzésre.');
      await loadParticipants();
      await loadEventDetails(); // Reload event details to update participant count
      onEventUpdated?.();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a jelentkezés során'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnregister = async () => {
    if (!eventId) return;

    // [Web UX 1. csomag, 2026-10-03] Megerősítés a leiratkozás előtt (eddig egy kattintás
    // azonnal DELETE-et küldött). A szöveg ideiglenes alapértelmezés: a pótlás-szabályt
    // NEM említi (klub-döntés). `destructive` → a „Mégse" kap fókuszt, Enter nem iratkoztat le.
    // A megerősítés az `actionLoading` ELŐTT fut: „Mégse" után a gombok nem ragadnak le.
    const confirmed = await confirm({
      title: 'Leiratkozás',
      message: 'Biztosan leiratkozol erről az edzésről? A helyed felszabadul.',
      confirmLabel: 'Leiratkozás',
      cancelLabel: 'Mégse',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      setActionLoading(true);
      setError('');
      await calendarService.unregisterFromEvent(eventId);
      showSuccess('Leiratkoztál az edzésről.');
      await loadParticipants();
      await loadEventDetails(); // Reload event details to update participant count
      onEventUpdated?.();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a leiratkozás során'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestSubstitution = async () => {
    if (!eventId || !eventDetails) return;

    const confirmed = await confirm({
      title: 'Helyettesítés kérése',
      message: 'Biztosan helyettesítést kér erre a magánórára? Minden edző és adminisztrátor értesítést kap.',
      confirmLabel: 'Helyettesítést kérek',
    });
    if (!confirmed) return;

    try {
      setActionLoading(true);
      setError('');
      await calendarService.requestSubstitution(eventId);
      showSuccess('A helyettesítési kérés elküldve.');
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a helyettesítési kérés küldése során'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!eventId || !eventDetails) return;

    const confirmed = await confirm({
      title: 'Esemény törlése',
      message: `Biztosan törölni szeretné a(z) "${eventDetails.eventName}" eseményt?`,
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await calendarService.deleteEvent(eventId);
      showSuccess('Az esemény törölve.');
      onDelete?.(eventId);
      onClose();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt az esemény törlésekor'));
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * [magánóra-sorozat, 2026-10-31, klub 4. döntés] A sorozat törlése „innentől" (ennek az alkalomnak a napjától,
   * bezárólag): EGY `DELETE /events/series/{groupId}?fromDate=`. A szerver kihagyja a kifizetett, a múltbeli, a
   * rögzített jelenlétű és a másik edzőhöz került alkalmat (`kept`), és játékosonként EGY értesítést küld.
   * Megerősítés a közös `ConfirmContext`-tel (natív `confirm` tilos); siker → toast, NINCS OK-ablak.
   */
  const handleDeleteSeries = async () => {
    if (!eventId || !eventDetails?.recurringEventGroupId) return;
    const fromDate = String(eventDetails.eventDate).slice(0, 10);

    const confirmed = await confirm({
      title: 'Sorozat törlése innentől',
      message:
        `A(z) "${eventDetails.eventName}" sorozat ${fromDate} napi és későbbi alkalmai törlődnek. ` +
        'A kifizetett és a már megtartott (jelenléttel rögzített) alkalmak megmaradnak. A résztvevők értesítést kapnak.',
      confirmLabel: 'Sorozat törlése',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const result = await calendarService.deleteSeries(eventDetails.recurringEventGroupId, fromDate);
      // [1. web-validátor M3] Ha egy alkalom sem törlődött (mind kifizetett / megtartott / elmúlt), az nem siker-üzenet.
      if ((result.deletedCount ?? 0) > 0) showSuccess(describeSeriesDeleteResult(result));
      else showInfo(describeSeriesDeleteResult(result));
      // A szülő (napi / havi nézet) erre zárja az ablakot és újratölti a naptárat.
      onDelete?.(eventId);
      onClose();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a sorozat törlésekor'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!eventId || !eventDetails) return;

    try {
      setActionLoading(true);
      const request: ApproveReservationRequest = {
        isApproved: true
      };
      await eventService.approveReservation(eventId, request);
      await loadEventDetails();
      onEventUpdated?.();
      showSuccess('A foglalás jóváhagyva.');
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a jóváhagyás során'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = () => {
    setShowRejectForm(true);
    setRejectionReason('');
    setError('');
  };

  const handleCancelReject = async () => {
    if (actionLoading) return;
    if (rejectReasonDirty && !(await confirm(discardChangesConfirm('elutasítás oka')))) return;
    setShowRejectForm(false);
    setRejectionReason('');
    setError('');
  };

  /**
   * A dialógus MINDEN záró útja (háttér, Esc, „Bezárás”, szerkesztésre váltás): beírt elutasítási
   * ok esetén kérdez, és a tényleges záráskor KIÜRÍTI az elutasítási űrlapot — korábban a
   * `showRejectForm` és a beírt ok a következő megnyitásra (akár MÁSIK eseményre) is átöröklődött.
   */
  const handleDialogClose = async () => {
    if (actionLoading) return;
    if (rejectReasonDirty && !(await confirm(discardChangesConfirm('elutasítás oka')))) return;
    setShowRejectForm(false);
    setRejectionReason('');
    onClose();
  };

  const handleRejectConfirm = async () => {
    if (!eventId || !eventDetails) return;

    if (!rejectionReason.trim()) {
      setError('Kérem adja meg az elutasítás okát');
      return;
    }

    try {
      setActionLoading(true);
      const request: ApproveReservationRequest = {
        isApproved: false,
        rejectionReason: rejectionReason
      };
      await eventService.approveReservation(eventId, request);
      await loadEventDetails();
      onEventUpdated?.();
      setShowRejectForm(false);
      setRejectionReason('');
      showSuccess('A foglalás elutasítva.');
      onClose(); // Close dialog after rejection
    } catch (err: any) {
      setError(saveErrorMessage(err, 'Hiba történt az elutasítás során'));
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (timeString: string): string => {
    return timeString.substring(0, 5);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const hungaryDayNames = ['Vasárnap', 'Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'];
    const hungaryMonthNames = [
      'január', 'február', 'március', 'április', 'május', 'június',
      'július', 'augusztus', 'szeptember', 'október', 'november', 'december'
    ];
    
    const dayName = hungaryDayNames[date.getDay()];
    const monthName = hungaryMonthNames[date.getMonth()];
    
    return `${date.getFullYear()}. ${monthName} ${date.getDate()}. (${dayName})`;
  };

  // Módosíthat: admin, VAGY (coach ÉS (a létrehozó VAGY az eseményhez rendelt edző)).
  // A backend CanModifyEvent szabállyal egyezik (player kizárva, típus-korlát nélkül).
  const canEdit = user && eventDetails && (
    user.userRole === 'Administrator' ||
    (user.userRole === 'Coach' && (
      user.userId === eventDetails.createdBy ||
      // A GÉPI névből, a megjelenítettre visszaesve [B-W4, 2026-09-16]: a display-név
      // átnevezhető, tehát arra nem lehet jogosultsági szabályt építeni.
      participants.some(p => p.userId === user.userId && isCoachRole(p.userRoleSystemName, p.userRoleName))
    ))
  );
  const canDelete = canEdit;

  /**
   * [magánóra-sorozat, 2026-10-31] „Sorozat törlése innentől": sorozat-tagon az adminnak mindig, az edzőnek csak a
   * SAJÁT sorozatán (a látható tagok mind az ő létrehozásai) — ugyanaz a szabály, mint a bővítésnél
   * (`EventEditDialog` `canExtendSeries`). A szerver a mérvadó (inaktív / nem látható tag → 403).
   */
  // [1. web-validátor F3 — koordinátori döntés] SABLON-CSOPORT nem sorozat: a régi egyedi `CreateEvent` sablonból
  // `RecurringEventGroupId = PlannedEventId`-t ír (`backend/PinyoApi/Controllers/EventController.cs:473`,
  // `RecurringEventGroupId ?? PlannedEventId`), így a sablonból EGYENKÉNT létrehozott események egy „csoportba" esnek.
  // Ezekre a „Sorozat törlése innentől" NEM jelenik meg (a sablon minden jövőbeli eseményét törölné). A bővítés és a
  // „minden alkalom" szerkesztés régi viselkedése változatlan.
  const isTemplateGroup = !!(eventDetails?.recurringEventGroupId && eventDetails?.plannedEventId
    && eventDetails.recurringEventGroupId.toLowerCase() === eventDetails.plannedEventId.toLowerCase());
  const canDeleteSeries = !!(user && eventDetails?.recurringEventGroupId && !isTemplateGroup && (
    user.userRole === 'Administrator' ||
    (user.userRole === 'Coach' && seriesMembers != null && isSeriesOwnedBy(seriesMembers, user.userId))
  ));

  // Helyettesítés: csak az eseményhez rendelt edző kérhet, magánórára, jövőbeli időpontra.
  const isAssignedCoach = !!(user && user.userRole === 'Coach' &&
    participants.some(p => p.userId === user.userId && isCoachRole(p.userRoleSystemName, p.userRoleName)));

  // Check if the event has started (is in the past)
  const isEventInPast = React.useMemo(() => {
    if (!eventDetails) return false;

    // Combine event date and start time to create a complete datetime
    const eventDateTime = new Date(`${eventDetails.eventDate}T${eventDetails.startTime}`);
    const now = new Date();

    return eventDateTime < now;
  }, [eventDetails]);

  // Check if the event is full (no available spots remaining)
  const isEventFull = React.useMemo(() => {
    if (!eventDetails) return false;

    // If maxParticipants is not set, event has unlimited capacity
    if (!eventDetails.maxParticipants) return false;

    // Event is full if participant count equals or exceeds max participants
    return eventDetails.participantCount >= eventDetails.maxParticipants;
  }, [eventDetails]);

  // Determine if registration button should be visible
  const canRegister = React.useMemo(() => {
    return !isEventInPast && !isEventFull && !isRegistered;
  }, [isEventInPast, isEventFull, isRegistered]);

  // Determine if unregistration button should be visible
  const canUnregister = React.useMemo(() => {
    return !isEventInPast && isRegistered;
  }, [isEventInPast, isRegistered]);

  /**
   * [Web 7 / T3 javító kör 1, 2026-10-06] TÖRÖLT esemény (lelet: scen-részletező). Egy elavult
   * naptárból megnyitott, KÖZBEN törölt esemény a `GET /events/{id}`-ben `isActive: false`-szal jön
   * (a törlés puha), és ez az ablak eddig rendes eseményként mutatta — Jelenlét, Jelentkezés,
   * Szerkesztés, Törlés gombokkal. A szerver ezeket ma mind elutasítja (jelenlét 409, jelentkezés
   * 404, szerkesztés 409, ismételt törlés no-op), a felhasználó tehát zsákutcába ment. Most: jól
   * látható figyelmeztetés, és a MŰVELETI gombok rejtve — a „Jelenlét" kivételével (javító kör 2,
   * M4: a törölt esemény pénze LÁTHATÓ marad, az ív megtekinthető, a mentése 409); az adatok (név, idő, résztvevők) olvashatók
   * maradnak, a „Bezárás" működik. A mobil mintája: `mobile/app/events/[id]/edit.tsx` `deletedOnServer`.
   * Csak a kifejezett `false` számít — egy régi, mezőt nem küldő válasz nem rejthet el semmit.
   */
  const isDeleted = eventDetails?.isActive === false;

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">
            Esemény részletei
          </Typography>
          {canEdit && eventDetails && !isDeleted && (
            <Box>
              {/* [Web UX 9 / T5, 2026-10-13] A Tooltip eddig a <span>-nak adott `aria-label`-t, a gomb maga név
                  nélküli volt (`EventDetailsDialog.tsx:414`). Most a GOMB kapja a nevet, a Tooltip `describeChild`-dal
                  csak leírás, így a név nem duplázódik.
                  [Web UX 9 / T5, 2026-10-13, validátor M5] Korlát, NEM megoldott: a `describeChild` az
                  `aria-describedby`-t a Tooltip közvetlen gyermekére, a `<span>`-ra teszi, és csak NYITOTT súgónál
                  (zárt súgónál a `<span>` natív `title`-t kap); a GOMB leírást így nem kap. Kifizetett eseménynél a gomb `disabled`, billentyűzettel (Tab) nem
                  érhető el, ezért a „Kifizetett esemény nem szerkeszthető" ok képernyőolvasóval most SEM hangzik el
                  — régen sem hangzott el (akkor a `<span>` `aria-label`-je volt, a letiltott gomb ugyanígy kimaradt). */}
              <Tooltip describeChild title={eventDetails.isPaidOut ? "Kifizetett esemény nem szerkeszthető" : "Esemény szerkesztése"}>
                <span>
                <IconButton
                  aria-label="Esemény szerkesztése"
                  onClick={async () => {
                    // A szerkesztőre váltás (a napi nézetben) BEZÁRJA ezt az ablakot — ugyanaz a kapu.
                    if (rejectReasonDirty && !(await confirm(discardChangesConfirm('elutasítás oka')))) return;
                    setShowRejectForm(false);
                    setRejectionReason('');
                    onEdit?.(eventDetails.eventId);
                  }}
                  disabled={actionLoading || eventDetails.isPaidOut}
                >
                  <EditIcon />
                </IconButton>
                </span>
              </Tooltip>
              {canDelete && !eventDetails.isPaidOut && (
                <Tooltip title="Esemény törlése">
                  <IconButton
                    onClick={handleDelete}
                    disabled={actionLoading}
                    color="error"
                    aria-label="Esemény törlése"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {!loading && isDeleted && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="event-deleted-alert">
            Ezt az eseményt időközben törölték.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : eventDetails ? (
          <Box>
            {/* Event Header */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                {eventDetails.eventName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={eventDetails.eventTypeDisplayName}
                  sx={{
                    backgroundColor: eventDetails.eventTypeColor,
                    color: 'white',
                  }}
                />
                {eventDetails.isPending && (
                  <Chip
                    label="Jóváhagyásra vár"
                    color="warning"
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
                {eventDetails.isPaidOut && (
                  <Chip
                    label="Kifizetett"
                    color="success"
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
              </Box>
            </Box>

            {/* Event Details */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  <strong>Dátum:</strong> {formatDate(eventDetails.eventDate)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  <strong>Időpont:</strong> {formatTime(eventDetails.startTime)} - {formatTime(eventDetails.endTime)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  <strong>Helyszín:</strong> {eventDetails.venueName}
                </Typography>
              </Box>

              {/* Participant count - only for non-table-reservation events */}
              {eventDetails.eventTypeName !== 'TableReservation' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body1">
                    <strong>Résztvevők:</strong> {eventDetails.participantCount}
                    {eventDetails.maxParticipants && (
                      <span> / {eventDetails.maxParticipants} fő ({eventDetails.participantCount}/{eventDetails.maxParticipants} hely foglalt)</span>
                    )}
                    {!eventDetails.maxParticipants && <span> fő</span>}
                  </Typography>
                </Box>
              )}

              {eventDetails.requiredTables && eventDetails.requiredTables > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TableIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body1">
                    <strong>Szükséges asztalok:</strong> {eventDetails.requiredTables} db
                  </Typography>
                </Box>
              )}

              {eventDetails.description && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <DescriptionIcon sx={{ mr: 1, mt: 0.5, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Leírás:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {eventDetails.description}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Only show participants for non-table-reservation events */}
            {eventDetails.eventTypeName !== 'TableReservation' && (
              <>
                <Divider sx={{ my: 2 }} />

                {/* Participants */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Résztvevők ({participants.length})
                  </Typography>

                  {participants.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Még nincs regisztrált résztvevő
                    </Typography>
                  ) : (
                    <List dense>
                      {participants.map((participant) => (
                        <ListItem key={participant.userId} divider>
                          {canViewProfiles ? (
                            <Link
                              component="button"
                              variant="body1"
                              onClick={() => handleNavigateToProfile(participant.userId)}
                              sx={{
                                textAlign: 'left',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                width: '100%',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              <ListItemText
                                primary={participant.userName}
                                secondary={participant.userRoleName}
                              />
                            </Link>
                          ) : (
                            <ListItemText
                              primary={participant.userName}
                              secondary={participant.userRoleName}
                            />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </>
            )}

            {/* Inline rejection form */}
            {showRejectForm && user && user.userRole === 'Administrator' && eventDetails.isPending && (
              /* [Web 7 / T2, 2026-10-06] WEB_UX_2KOR (`Documents/WEB_UX_JAVASLATOK_2KOR_2026-10-04.md`) 4.1: az `error.lighter` palettakulcs a MUI 7.3.2-ben NEM létezik →
                  a panel háttere sosem jelent meg; az `error.dark` cím sötét témában 1,97:1, a `bgcolor: 'white'` szövegmező
                  sötét témában világos betűvel fehéren 1,12:1 volt. Közös, kontraszt-őrrel mért recept: `rejectionPanelColors`.
                  [Web 7 / T2 javító kör 2, 2026-10-06 — M-E] Pontosítás: az 1,12:1 a szövegmező GYÖKERÉNEK (fehér `bgcolor`) felszínére igaz;
                  sötétben a beírt szöveg valójában a `.MuiOutlinedInput-root` saját `#242A30` hátterén jelenik meg (`darkTheme.ts` MuiTextField felülírás). */
              <Box data-testid="rejection-panel" sx={{ mt: 3, p: 2, bgcolor: (t: Theme) => rejectionPanelColors(t).bg, borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: (t: Theme) => rejectionPanelColors(t).title }}>
                  Elutasítás oka
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Kérem adja meg az elutasítás okát, amit a felhasználó látni fog..."
                  variant="outlined"
                  data-testid="rejection-reason-field"
                  sx={{ mb: 2, bgcolor: (t: Theme) => rejectionPanelColors(t).field }} /* [Web 7 / T2 javító kör 1, 2026-10-06 — F3] a szövegmező háttere is a közös receptből */
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancelReject}
                  >
                    Mégse
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={handleRejectConfirm}
                    disabled={!rejectionReason.trim() || actionLoading}
                  >
                    Elutasítás
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Box>
            {/*
              [Web UX 1. csomag, 2026-10-03] Állapotsor: „Jelentkeztél erre az edzésre".
              Feltétel: PONTOSAN ott látszik, ahol a „Leiratkozás" gomb (`canUnregister`
              + nem függő + nem asztalfoglalás) — így a sor és a gomb soha nem mond
              ellent egymásnak. Múltbeli eseményen szándékosan NINCS (ott a „megkezdődött"
              magyarázó szöveg szól, és leiratkozni sem lehet).
              Az `isRegistered` a résztvevő-listából jön (`loadParticipants`): a
              `GET /events/{id}/participants` csak az `UnregisteredAt == null` sorokat adja
              — ugyanaz a fogalom, mint a naptár `IsRegistered` mezője (CalendarController).
              `role="status"`: a felolvasó udvariasan jelzi, nem szakít félbe (az Alert
              alapból `role="alert"`).
            */}
            {eventDetails && user && !isDeleted && canUnregister && !eventDetails.isPending &&
              eventDetails.eventTypeName !== 'TableReservation' && (
              <Alert
                severity="success"
                role="status"
                data-testid="registered-status"
                sx={{ mb: 1, py: 0 }}
              >
                Jelentkeztél erre az edzésre
              </Alert>
            )}
            {/*
              [Web 7 / T3 javító kör 1] törölt eseményen NINCS műveleti gomb (lásd `isDeleted`) —
              [Web 7 / T3 javító kör 2, 2026-10-06] lelet M4: KIVÉVE a „Jelenlét" gombot. A user
              döntése: „a törölt esemény pénze LÁTHATÓ marad"; a beszedett helyszíni díjakat a
              jelenléti ív mutatja, így a gomb nélkül a részletezőből elérhetetlen lenne. Az ív
              megnyitható (megtekintés), a mentést a szerver 409-cel és érthető üzenettel utasítja
              el (`AttendanceController.DeletedEventAttendanceMessage`).
            */}
            {eventDetails && user && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/*
                  Jelenlét — ugyanaz a szabály, mint mobilon (mobile/app/events/[id].tsx):
                  admin és edző látja, függetlenül attól, hogy ő-e az esemény edzője, és
                  akkor is, ha az esemény már elmúlt (a jelenlétet utólag szokás rögzíteni).

                  ELTÉRÉS a mobiltól: asztalfoglalásnál NINCS gomb. A jelenléti ív szándéka
                  szerint kizárja az asztalfoglalást („no attendance tracking needed"), és ez
                  a fájl is kizárja a jelentkezés-gombokból — a gomb ott zsákutcába vinne.
                */}
                {onNavigateToAttendance &&
                  (user.userRole === 'Administrator' || user.userRole === 'Coach') &&
                  eventDetails.eventTypeName !== 'TableReservation' && (
                  <Button
                    startIcon={<AttendanceIcon />}
                    onClick={() => {
                      onNavigateToAttendance(eventDetails.eventId, eventDetails.eventDate);
                      onClose();
                    }}
                    disabled={actionLoading}
                    variant="contained"
                  >
                    Jelenlét
                  </Button>
                )}

                {/* Admin approval buttons for pending reservations */}
                {!isDeleted && user.userRole === 'Administrator' && eventDetails.isPending && !showRejectForm && (
                  <>
                    <Button
                      startIcon={<ApproveIcon />}
                      onClick={handleApprove}
                      disabled={actionLoading}
                      variant="contained"
                      color="success"
                    >
                      Jóváhagyás
                    </Button>
                    <Button
                      startIcon={<RejectIcon />}
                      onClick={handleRejectClick}
                      disabled={actionLoading}
                      variant="contained"
                      color="error"
                    >
                      Elutasítás
                    </Button>
                  </>
                )}

                {/* Helyettesítés kérése: csak az eseményhez rendelt edzőnek, jövőbeli magánórán */}
                {!isDeleted && isAssignedCoach && eventDetails.eventTypeName === 'IndividualTraining' && !isEventInPast && (
                  <Button
                    startIcon={<SubstitutionIcon />}
                    onClick={handleRequestSubstitution}
                    disabled={actionLoading}
                    variant="outlined"
                    color="warning"
                  >
                    Helyettesítést kérek
                  </Button>
                )}

                {/* [magánóra-sorozat, 2026-10-31] Sorozat törlése innentől (admin; edző a saját sorozatán). */}
                {!isDeleted && canDeleteSeries && (
                  <Button
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteSeries}
                    disabled={actionLoading}
                    variant="outlined"
                    color="error"
                    data-testid="series-delete-from-here"
                  >
                    Sorozat törlése innentől
                  </Button>
                )}

                {/* Regular registration buttons for non-pending and non-table-reservation events */}
                {!isDeleted && !eventDetails.isPending && eventDetails.eventTypeName !== 'TableReservation' && (
                  <>
                    {canRegister && (
                      <Button
                        startIcon={<RegisterIcon />}
                        onClick={handleRegister}
                        disabled={actionLoading}
                        variant="contained"
                        color="success"
                      >
                        Jelentkezés
                      </Button>
                    )}
                    {canUnregister && (
                      <Button
                        startIcon={<UnregisterIcon />}
                        onClick={handleUnregister}
                        disabled={actionLoading}
                        variant="outlined"
                        color="warning"
                      >
                        Leiratkozás
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Explanatory text for different scenarios (only for non-table-reservation events) */}
            {eventDetails && !isDeleted && eventDetails.eventTypeName !== 'TableReservation' && (
              <>
                {isEventInPast && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Az esemény már megkezdődött, regisztráció nem lehetséges
                  </Typography>
                )}
                {!isEventInPast && isEventFull && !isRegistered && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Az esemény betelt, regisztráció nem lehetséges
                  </Typography>
                )}
                {!isEventInPast && !isEventFull && !isRegistered && !user && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Regisztrációhoz bejelentkezés szükséges
                  </Typography>
                )}
              </>
            )}
          </Box>

          <Button onClick={handleDialogClose} disabled={actionLoading}>
            Bezárás
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EventDetailsDialog;